import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  RefreshCw, 
  Trash2, 
  Clock, 
  AlertCircle, 
  CheckCircle2, 
  XCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
  Wifi,
  WifiOff,
  CloudOff,
  Cloud
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import {
  getSyncQueue,
  removeFromSyncQueue,
  clearSyncQueue,
  processSyncQueue,
  subscribeSyncQueue,
} from '@/lib/backgroundSync';

interface SyncRequest {
  id: string;
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: string;
  timestamp: number;
  retryCount: number;
  maxRetries: number;
  context?: string;
}

interface SyncQueueVisualizationProps {
  className?: string;
  compact?: boolean;
}

// Status badge component
const StatusBadge = ({ retryCount, maxRetries }: { retryCount: number; maxRetries: number }) => {
  const progress = (retryCount / maxRetries) * 100;
  
  if (retryCount === 0) {
    return (
      <Badge variant="secondary" className="gap-1">
        <Clock className="h-3 w-3" />
        Pending
      </Badge>
    );
  }
  
  if (retryCount >= maxRetries) {
    return (
      <Badge variant="destructive" className="gap-1">
        <XCircle className="h-3 w-3" />
        Failed
      </Badge>
    );
  }
  
  return (
    <Badge variant="outline" className="gap-1 text-yellow-600 border-yellow-600/50">
      <AlertCircle className="h-3 w-3" />
      Retry {retryCount}/{maxRetries}
    </Badge>
  );
};

// Single queue item component
const QueueItem = ({
  item,
  onRetry,
  onRemove,
  isRetrying,
}: {
  item: SyncRequest;
  onRetry: (id: string) => void;
  onRemove: (id: string) => void;
  isRetrying: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const progress = ((item.maxRetries - item.retryCount) / item.maxRetries) * 100;
  
  // Parse URL for display
  const url = new URL(item.url, window.location.origin);
  const endpoint = url.pathname.split('/').pop() || url.pathname;
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className="border rounded-lg p-3 bg-card hover:bg-accent/50 transition-colors"
    >
      <div className="flex items-start gap-3">
        {/* Method badge */}
        <Badge
          variant="outline"
          className={cn(
            'text-xs font-mono',
            item.method === 'GET' && 'text-blue-600 border-blue-600/50',
            item.method === 'POST' && 'text-green-600 border-green-600/50',
            item.method === 'PUT' && 'text-yellow-600 border-yellow-600/50',
            item.method === 'DELETE' && 'text-red-600 border-red-600/50'
          )}
        >
          {item.method}
        </Badge>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate">{item.context || endpoint}</span>
            <StatusBadge retryCount={item.retryCount} maxRetries={item.maxRetries} />
          </div>
          
          <p className="text-xs text-muted-foreground mt-1">
            {formatDistanceToNow(item.timestamp, { addSuffix: true })}
          </p>
          
          {/* Progress bar for retries */}
          {item.retryCount > 0 && item.retryCount < item.maxRetries && (
            <Progress value={progress} className="h-1 mt-2" />
          )}
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => onRetry(item.id)}
                  disabled={isRetrying}
                >
                  {isRetrying ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Retry now</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => onRemove(item.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Remove from queue</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>
      
      {/* Expanded details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-3 pt-3 border-t text-xs space-y-2">
              <div>
                <span className="text-muted-foreground">URL: </span>
                <code className="bg-muted px-1 py-0.5 rounded break-all">{item.url}</code>
              </div>
              {item.body && (
                <div>
                  <span className="text-muted-foreground">Body: </span>
                  <pre className="bg-muted px-2 py-1 rounded mt-1 overflow-x-auto text-xs">
                    {JSON.stringify(JSON.parse(item.body), null, 2).slice(0, 200)}
                    {item.body.length > 200 && '...'}
                  </pre>
                </div>
              )}
              <div className="flex gap-4">
                <span>
                  <span className="text-muted-foreground">ID: </span>
                  <code className="bg-muted px-1 py-0.5 rounded">{item.id.slice(0, 12)}...</code>
                </span>
                <span>
                  <span className="text-muted-foreground">Retries: </span>
                  {item.retryCount}/{item.maxRetries}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const SyncQueueVisualization = ({
  className,
  compact = false,
}: SyncQueueVisualizationProps) => {
  const [queue, setQueue] = useState<SyncRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(!compact);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Load queue and subscribe to changes
  useEffect(() => {
    const loadQueue = async () => {
      setIsLoading(true);
      try {
        const items = await getSyncQueue();
        setQueue(items);
      } catch (error) {
        console.error('Failed to load sync queue:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadQueue();

    // Subscribe to queue changes
    const unsubscribe = subscribeSyncQueue(async () => {
      const items = await getSyncQueue();
      setQueue(items);
    });

    // Online status listener
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle retry all
  const handleRetryAll = async () => {
    if (!isOnline) return;
    setIsSyncing(true);
    try {
      await processSyncQueue();
      const items = await getSyncQueue();
      setQueue(items);
    } finally {
      setIsSyncing(false);
    }
  };

  // Handle single retry
  const handleRetry = async (id: string) => {
    if (!isOnline) return;
    setRetryingId(id);
    try {
      await processSyncQueue();
      const items = await getSyncQueue();
      setQueue(items);
    } finally {
      setRetryingId(null);
    }
  };

  // Handle remove
  const handleRemove = async (id: string) => {
    await removeFromSyncQueue(id);
    setQueue((prev) => prev.filter((item) => item.id !== id));
  };

  // Handle clear all
  const handleClearAll = async () => {
    await clearSyncQueue();
    setQueue([]);
  };

  // Stats
  const pendingCount = queue.filter((q) => q.retryCount === 0).length;
  const retryingCount = queue.filter((q) => q.retryCount > 0 && q.retryCount < q.maxRetries).length;
  const failedCount = queue.filter((q) => q.retryCount >= q.maxRetries).length;

  if (isLoading) {
    return (
      <Card className={cn('', className)}>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Compact mode - just show a badge/button
  if (compact && queue.length === 0) {
    return null;
  }

  if (compact) {
    return (
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded} className={className}>
        <CollapsibleTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'gap-2 w-full justify-between',
              !isOnline && 'border-yellow-500/50'
            )}
          >
            <span className="flex items-center gap-2">
              {isOnline ? (
                <Cloud className="h-4 w-4 text-green-500" />
              ) : (
                <CloudOff className="h-4 w-4 text-yellow-500" />
              )}
              Sync Queue
              {queue.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {queue.length}
                </Badge>
              )}
            </span>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <Card>
            <CardContent className="pt-4">
              <ScrollArea className="max-h-[300px]">
                <div className="space-y-2">
                  <AnimatePresence mode="popLayout">
                    {queue.map((item) => (
                      <QueueItem
                        key={item.id}
                        item={item}
                        onRetry={handleRetry}
                        onRemove={handleRemove}
                        isRetrying={retryingId === item.id || isSyncing}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </ScrollArea>
              
              {queue.length > 0 && (
                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    onClick={handleRetryAll}
                    disabled={!isOnline || isSyncing}
                    className="flex-1"
                  >
                    {isSyncing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Sync All
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleClearAll}
                    className="text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  // Full mode
  return (
    <Card className={cn('', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            {isOnline ? (
              <Wifi className="h-5 w-5 text-green-500" />
            ) : (
              <WifiOff className="h-5 w-5 text-yellow-500" />
            )}
            Sync Queue
          </CardTitle>
          
          {/* Stats badges */}
          <div className="flex gap-2">
            {pendingCount > 0 && (
              <Badge variant="secondary" className="gap-1">
                <Clock className="h-3 w-3" />
                {pendingCount} pending
              </Badge>
            )}
            {retryingCount > 0 && (
              <Badge variant="outline" className="gap-1 text-yellow-600 border-yellow-600/50">
                <AlertCircle className="h-3 w-3" />
                {retryingCount} retrying
              </Badge>
            )}
            {failedCount > 0 && (
              <Badge variant="destructive" className="gap-1">
                <XCircle className="h-3 w-3" />
                {failedCount} failed
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {queue.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">All synced!</p>
            <p className="text-sm">No pending requests in the queue</p>
          </div>
        ) : (
          <>
            <ScrollArea className="max-h-[400px] pr-2">
              <div className="space-y-2">
                <AnimatePresence mode="popLayout">
                  {queue.map((item) => (
                    <QueueItem
                      key={item.id}
                      item={item}
                      onRetry={handleRetry}
                      onRemove={handleRemove}
                      isRetrying={retryingId === item.id || isSyncing}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </ScrollArea>
            
            <div className="flex gap-2 mt-4 pt-4 border-t">
              <Button
                onClick={handleRetryAll}
                disabled={!isOnline || isSyncing}
                className="flex-1"
              >
                {isSyncing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {isOnline ? 'Sync All Now' : 'Waiting for connection...'}
              </Button>
              <Button
                variant="outline"
                onClick={handleClearAll}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Compact badge version for navbar/header
export const SyncQueueBadge = ({ className }: { className?: string }) => {
  const [count, setCount] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const unsubscribe = subscribeSyncQueue(setCount);
    
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (count === 0 && isOnline) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium',
              isOnline 
                ? 'bg-primary/10 text-primary' 
                : 'bg-yellow-500/10 text-yellow-600',
              className
            )}
          >
            {isOnline ? (
              <RefreshCw className="h-3 w-3" />
            ) : (
              <CloudOff className="h-3 w-3" />
            )}
            {count > 0 && <span>{count}</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {isOnline 
            ? `${count} request${count !== 1 ? 's' : ''} pending sync`
            : 'Offline - requests will sync when connected'
          }
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default SyncQueueVisualization;
